// react core
import { Fragment, useContext, useState, useEffect } from "react";

// material design
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grow from "@mui/material/Grow";
import Backdrop from "@mui/material/Backdrop";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import SpeedDialAction from "@mui/material/SpeedDialAction";

// fonts and icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle as iconCircle } from "@fortawesome/pro-solid-svg-icons";
import { faSquare as iconSquare } from "@fortawesome/pro-solid-svg-icons";
import { faArrowRight as iconArrow } from "@fortawesome/pro-solid-svg-icons";

// entzy config and context
import configEntzy from "components/config/ConfigEntzy";
import { MainContext } from "components/main/MainContext";
import { EventContext } from "components/event/EventContext";
import { createEventModuleList } from "models/event";
import { parseEntzyUrlComponents } from "services/tools";

// derive active modules
const ACTIVE_MODULES = createEventModuleList().filter((obj) => obj.active);
const CORE_MODULES = createEventModuleList().filter(
  (obj) => obj.active && !obj.secondaryNav,
);
const SECONDARY_MODULES = createEventModuleList().filter(
  (obj) => obj.active && obj.secondaryNav,
);
const VISITING_RIDER_ACTIVE_MODULES = createEventModuleList().filter(
  (obj) => obj.active && obj.visitingRider,
);
const BOOKEND_MODULES = createEventModuleList().filter(
  (obj) => obj.active && obj.bookend,
);

function getModules(user, manager, filters) {
  let modules = ACTIVE_MODULES;
  if (!user.connected && !manager) {
    modules = VISITING_RIDER_ACTIVE_MODULES;
  }
  if (filters) {
    if (filters.core) {
      modules = CORE_MODULES;
    }
    if (filters.secondaryNav) {
      modules = SECONDARY_MODULES;
    }
    if (filters.bookend) {
      modules = BOOKEND_MODULES;
    }
  }
  return modules;
}

function getModuleCounter(moduleId, eventUrl, notifications, eventRiderQueue) {
  const counterExcludeActivityCategories = ["activity:eventuate"];

  let counterShow, counterValue;
  if (moduleId === "messaging") {
    counterShow = true;
    counterValue = notifications.data.messages.filter(
      (obj) =>
        obj.MessageUrl.includes(
          parseEntzyUrlComponents(eventUrl).relativeUrl,
        ) && !obj.MessageRead,
    ).length;
  } else if (moduleId === "entry") {
    counterShow = true;
    counterValue = notifications.data.activity.filter(
      (obj) =>
        obj.MessageUrl.includes(
          parseEntzyUrlComponents(eventUrl).relativeUrl,
        ) &&
        !obj.MessageRead &&
        !counterExcludeActivityCategories.includes(obj.MessageCategory),
    ).length;
  } else if (moduleId === "members") {
    counterValue = eventRiderQueue ? eventRiderQueue.data.items.length : 0;
    counterShow = counterValue > 0 ? true : false;
  } else {
    counterShow = false;
    counterValue = 0;
  }
  return [counterShow, counterValue];
}

function ModuleButton(props) {
  const user = props.user;
  const mainContext = useContext(MainContext);
  const eventContext = useContext(EventContext);
  const module = props.module;
  const isCircleMenu = props.sourceMenu === "circular";
  const isCircleButton = props.filters && props.filters.circle;
  const isSquareButton = props.filters && props.filters.square;
  const isButton = isCircleButton || isSquareButton;
  const isMini = props.filters && props.filters.mini;
  const isDark = props.filters && props.filters.dark;
  const showTips = props.filters && props.filters.tips;
  const showNumbers = props.filters && props.filters.numbers;
  const hideText = props.filters && props.filters.hideText;
  const floatText = props.filters && props.filters.floatText;
  const animateText = props.filters && props.filters.animateText;
  const noText = props.filters && props.filters.noText;
  const smallIcons = props.filters && props.filters.smallIcons;

  let [counterShow, counterValue] = getModuleCounter(
    module.id,
    eventContext.state.event.data.Url,
    mainContext.state.notifications,
    eventContext.state.riderQueue,
  );

  const [animate, setAnimate] = useState(0);

  // toggle animation on a 6 second delay for N times
  useEffect(() => {
    const timer = setInterval(() => {
      setAnimate(animate + 1);
    }, 6000);
    if (animate > 21) {
      clearInterval(timer);
    }
    return () => clearInterval(timer);
  }, [animate]);

  const openTransact = async () => {
    if (user.connected) {
      mainContext.setPayDirectViewer({
        open: mainContext.state.payDirect.viewer.open ? false : true,
        recipient: parseEntzyUrlComponents(eventContext.state.event.data.Url)
          .relativeUrl,
        currency: eventContext.state.event.data.Currency,
      });
    } else {
      mainContext.updateAlert({
        show: true,
        message: "You must be connected to transact",
      });
    }
  };

  const handleClick = () => {
    if (module.actionExit) {
      eventContext.exitEvent({ mainContext, eventContext });
    } else if (module.id === "transact") {
      openTransact();
    } else if (module.id === "settings") {
      mainContext.updateViewerTrail(
        eventContext.state.event.manager
          ? {
              showActionSettings:
                !mainContext.state.viewer.trail.showActionSettings,
            }
          : {
              showFrontDeskSettings:
                !mainContext.state.viewer.trail.showFrontDeskSettings,
            },
      );
    } else if (module.id === "menu" && props.menuAction) {
      props.menuAction();
    } else {
      eventContext.focusModule(module.id);
    }
  };

  return (
    <Box
      className="box-inline action-pointer"
      sx={{
        color:
          eventContext.state.event.moduleSelected === module.id || isDark
            ? "orange"
            : "grey",
        whiteSpace: "nowrap",
      }}
      onClick={handleClick}
    >
      <Box className="box-default">
        {showTips && module.tipText && (
          <Box
            className="box-abs bg-black-t50 pop-top"
            sx={{
              width: configEntzy.AVATAR_SIZE_MD,
              left:
                isCircleMenu && props.index > 2
                  ? configEntzy.APP_SPACING_XS
                  : undefined,
              right:
                isCircleMenu && props.index < 3
                  ? configEntzy.APP_SPACING_XS
                  : undefined,
              mt: configEntzy.APP_SPACING_XL,
              ml: !isCircleMenu ? configEntzy.APP_SPACING_NMD2X : undefined,
              p: configEntzy.APP_SPACING_SM,
              borderRadius: configEntzy.BORDER_SIZE_LG,
              color: "white",
            }}
          >
            <Typography variant="body1">
              {eventContext.state.event.manager
                ? module.tipText.runners
                : module.tipText.riders}
            </Typography>
          </Box>
        )}
        <Typography
          variant={smallIcons ? "subtitle2" : "h6"}
          sx={{
            mt: isMini ? 0 : configEntzy.APP_SPACING_LG,
            color:
              eventContext.state.event.moduleSelected === module.id || isDark
                ? "orange"
                : "white",
          }}
        >
          <span className="fa-layers fa-3x fa-fw">
            {(isButton ||
              eventContext.state.event.moduleSelected === module.id) && (
              <FontAwesomeIcon
                icon={
                  isCircleButton
                    ? eventContext.state.event.moduleSelected === module.id
                      ? iconCircle
                      : iconCircle
                    : eventContext.state.event.moduleSelected === module.id
                      ? iconSquare
                      : iconSquare
                }
                transform={isMini ? "grow-2" : "grow-8"}
                color={
                  eventContext.state.event.moduleSelected === module.id ||
                  isDark
                    ? "orange"
                    : "black"
                }
                style={{
                  opacity:
                    eventContext.state.event.moduleSelected === module.id
                      ? 1
                      : 0.8,
                  filter: "drop-shadow(0.1rem 0.1rem 0.1rem black)",
                }}
              />
            )}
            {showNumbers && !showTips && module.icon.number && (
              <FontAwesomeIcon
                icon={module.icon.number}
                transform={isCircleMenu ? "up-20 left-10" : "up-20"}
              />
            )}
            <FontAwesomeIcon
              icon={
                module.icon.runner && eventContext.state.event.manager
                  ? module.icon.runner
                  : module.icon.rider && !eventContext.state.event.manager
                    ? module.icon.rider
                    : module.icon.main
              }
              transform={isMini ? "shrink-8" : "shrink-6"}
              color={
                eventContext.state.event.moduleSelected === module.id
                  ? "black"
                  : "white"
              }
            />
            {counterShow && (
              <span
                className="fa-layers-counter fa-layers-top-right"
                style={{
                  backgroundColor: counterValue > 0 ? "red" : "white",
                  color: counterValue > 0 ? "white" : "black",
                  border:
                    counterValue > 0 ? "1px solid red" : "1px solid black",
                }}
              >
                {counterValue}
              </span>
            )}
          </span>
        </Typography>
      </Box>
      <Box className="box-default relative">
        <Box
          className="box-default bg-black shadow-orange rounded"
          hidden={
            noText ||
            (hideText && eventContext.state.event.moduleSelected !== module.id)
          }
          sx={{
            zIndex: 9,
            position: floatText ? "absolute" : undefined,
            width: floatText ? "max-content" : undefined,
            left: floatText ? "50%" : undefined,
            transform: floatText ? "translate(-50%, -50%)" : undefined,
          }}
        >
          {animateText ? (
            <Fragment>
              <Grow in={animate % 2 === 0} timeout={2000}>
                <Box className="box-default zero-height">
                  <Typography variant="body2">
                    &nbsp;&nbsp;{module.shortName}&nbsp;&nbsp;
                  </Typography>
                </Box>
              </Grow>
              <Grow in={animate % 2 !== 0} timeout={2000}>
                <Box className="box-default zero-height">
                  <Typography variant="body2">
                    &nbsp;&nbsp;{module.shortSubtext}&nbsp;&nbsp;
                  </Typography>
                </Box>
              </Grow>
              <Box className="box-default">
                <Typography variant="body2">&nbsp;</Typography>
              </Box>
            </Fragment>
          ) : (
            <Box className="box-default">
              <Typography variant="body2">
                &nbsp;&nbsp;{module.shortName}&nbsp;&nbsp;
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export function MenuEventInfo(props) {
  const mainContext = useContext(MainContext);
  const eventContext = useContext(EventContext);
  const user = props.user;
  const lightMode = props.light;
  const modules = getModules({ connected: true }, false, { core: true });
  const menuModule = createEventModuleList().filter(
    (obj) => obj.id === "menu",
  )[0];

  return (
    <Box
      id="anchor-menu-event"
      className="box-default full-height"
      sx={{
        p: configEntzy.APP_SPACING_SM,
      }}
    >
      {/* MENU BUTTON */}
      <Box
        className="box-default text-left"
        sx={{
          mt: configEntzy.APP_SPACING_LG,
          pl: configEntzy.APP_SPACING_MD3X,
        }}
      >
        <Typography variant="h1" sx={{ overflow: "visible" }}>
          <span
            className="fa-layers fa-fw action-pointer"
            onClick={() => eventContext.focusModule("menu")}
          >
            <FontAwesomeIcon
              icon={iconCircle}
              transform="grow-10 down-8 left-1"
              style={{ color: "black" }}
            />
            <FontAwesomeIcon
              icon={menuModule.icon.main}
              transform="down-8 left-1"
              style={{ color: "white" }}
            />
          </span>
        </Typography>
        <Typography
          variant="subtitle2"
          color={lightMode ? "white" : "black"}
          sx={{
            mt: configEntzy.APP_SPACING_LG,
          }}
        >
          {menuModule.shortName} Information
        </Typography>
        <Typography variant="subtitle2" color={lightMode ? "white" : "black"}>
          Follow the eventuator brick road...
        </Typography>
      </Box>
      {/* INFO ON EACH CORE MENU ITEM */}
      <Box className="box-default">
        {modules.map((module, index) => {
          return (
            <Box
              key={"nav-" + module.id}
              className={
                eventContext.state.event.moduleSelected === module.id
                  ? "box-default bg-white rounded"
                  : "box-default action-pointer"
              }
              onClick={
                module.actionExit
                  ? () => eventContext.exitEvent({ mainContext, eventContext })
                  : () => eventContext.focusModule(module.id)
              }
              sx={{
                width: Math.floor((100 / CORE_MODULES.length) * 2) + "%",
                display: "inline-block",
                p: configEntzy.APP_SPACING_XS2X,
              }}
            >
              <Box
                className="box-default"
                sx={{
                  pt: configEntzy.APP_SPACING_LG2X,
                  pl: configEntzy.APP_SPACING_XS,
                  pr: configEntzy.APP_SPACING_XS,
                }}
              >
                <Box className="box-default">
                  <Typography
                    variant="subtitle2"
                    sx={{
                      overflow: "visible",
                      color: lightMode ? "white" : "black",
                      filter: "drop-shadow(0.1rem 0.1rem 0.1rem black)",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={module.icon.number}
                      size="3x"
                      className="fa-beat"
                      style={{
                        "--fa-animation-duration": "1.5s",
                        "--fa-beat-scale": 1.1,
                      }}
                      transform="up-5"
                      color="white"
                    />
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      overflow: "visible",
                      color: lightMode ? "white" : "black",
                      mt: configEntzy.APP_SPACING_MD2X,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={module.icon.main}
                      size="3x"
                      className="fa-beat"
                      style={{
                        "--fa-animation-duration": "1.5s",
                        "--fa-beat-scale": 1.1,
                      }}
                      transform="up-5"
                    />
                  </Typography>
                </Box>
                <Box
                  className="box-default upper-case"
                  sx={{
                    pt: configEntzy.APP_SPACING_MD,
                  }}
                  hidden={props.footer}
                >
                  <Box className="box-default">
                    <Typography
                      variant="subtitle2"
                      color={lightMode ? "white" : "black"}
                    >
                      {module.shortName}
                    </Typography>
                  </Box>
                  <Box className="box-default">
                    <Box
                      className="box-inline bg-black-t50"
                      sx={{
                        mt: configEntzy.APP_SPACING_SM,
                        pt: configEntzy.APP_SPACING_XS,
                        pb: configEntzy.APP_SPACING_XS,
                        pl: configEntzy.APP_SPACING_SM,
                        pr: configEntzy.APP_SPACING_SM,
                        borderRadius: configEntzy.APP_SPACING_SM,
                      }}
                    >
                      <Typography variant="body2" color="primary">
                        &nbsp;{module.shortSubtext}&nbsp;
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  className="box-default"
                  sx={{
                    mt: configEntzy.APP_SPACING_MD,
                  }}
                >
                  <Box
                    className="box-default half-width"
                    sx={{
                      height: [0, 3].includes(index)
                        ? configEntzy.APP_SPACING_XL
                        : [1, 4].includes(index)
                          ? configEntzy.APP_SPACING_XL2X
                          : configEntzy.APP_SPACING_XL3X,
                      borderRight: lightMode
                        ? configEntzy.BORDER_DASH_DSM
                        : configEntzy.BORDER_DASH_GSM,
                    }}
                  ></Box>
                  <Box
                    className="box-default half-width"
                    sx={{
                      height: [0, 3].includes(index)
                        ? configEntzy.APP_SPACING_XL
                        : [1, 4].includes(index)
                          ? configEntzy.APP_SPACING_XL2X
                          : configEntzy.APP_SPACING_XL3X,
                    }}
                  ></Box>
                  <Box
                    className="box-default bg-white-t50"
                    sx={{
                      p: configEntzy.APP_SPACING_SM2X,
                      border: lightMode
                        ? configEntzy.BORDER_DASH_DSM
                        : configEntzy.BORDER_DASH_GSM,
                      borderRadius: configEntzy.BORDER_SIZE_XL,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      color={lightMode ? "white" : "black"}
                    >
                      {module.help}
                    </Typography>
                    {!user.connected && index > 2 && (
                      <Typography
                        variant="subtitle2"
                        color={lightMode ? "white" : "black"}
                        sx={{
                          mt: configEntzy.APP_SPACING_MD,
                        }}
                      >
                        <em>Connect to access</em>
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
      {/* CONTINUE BUTTON */}
      <Box
        className="box-default text-right"
        sx={{
          mt: configEntzy.APP_SPACING_LG2X,
          pr: configEntzy.APP_SPACING_MD3X,
        }}
      >
        <Typography variant="h1" color={lightMode ? "white" : "black"}>
          <span
            className="fa-layers fa-fw action-pointer"
            onClick={() => eventContext.focusModule("launchpad")}
          >
            <FontAwesomeIcon
              icon={iconCircle}
              transform="grow-10 up-9"
              style={{ color: "black" }}
            />
            <FontAwesomeIcon
              icon={iconArrow}
              transform="up-9"
              style={{ color: "white" }}
            />
          </span>
        </Typography>
      </Box>
    </Box>
  );
}

// mini nav bar for use on all modules except menu
export function MenuEventCore(props) {
  const user = props.user;
  const mainContext = useContext(MainContext);
  const eventContext = useContext(EventContext);
  const modules = getModules(
    user,
    eventContext.state.event.manager,
    props.filters,
  );

  return (
    <Box className="box-default">
      {modules.map((module, index) => {
        return (
          <Box
            key={"nav-" + module.id}
            className={
              eventContext.state.event.moduleSelected === module.id
                ? "box-inline bg-white rounded"
                : "box-inline action-pointer"
            }
            onClick={
              module.actionExit
                ? () => eventContext.exitEvent({ mainContext, eventContext })
                : () => eventContext.focusModule(module.id)
            }
            sx={{
              width: Math.floor(100 / CORE_MODULES.length) + "%",
            }}
          >
            <Box
              className={
                eventContext.state.event.moduleSelected === module.id
                  ? "box-inline bg-white rounded"
                  : "box-inline action-pointer"
              }
              sx={{ p: configEntzy.APP_SPACING_SM }}
            >
              <Typography variant="subtitle2">
                <FontAwesomeIcon icon={module.icon.main} size="2x" fixedWidth />
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// top header nav for menu and exit
export function MenuEventBar(props) {
  const user = props.user;
  const eventContext = useContext(EventContext);
  const modules = getModules(
    user,
    eventContext.state.event.manager,
    props.filters,
  );
  return (
    <Box
      className="box-default"
      sx={{
        mt: configEntzy.APP_SPACING_SM,
        pt: configEntzy.APP_SPACING_MD,
        pb: configEntzy.APP_SPACING_MD,
      }}
    >
      <Container maxWidth="lg" disableGutters>
        {modules.map((module, index) => {
          return (
            <Box
              key={"nav-" + module.id}
              className="box-inline"
              sx={{
                width: Math.floor(100 / modules.length) + "%",
              }}
            >
              {props.filters.onlyModules &&
              !props.filters.onlyModules.includes(module.id) ? (
                <></>
              ) : (
                <ModuleButton
                  {...props}
                  key={module.id}
                  module={module}
                  index={index}
                  sourceMenu="bar"
                />
              )}
            </Box>
          );
        })}
      </Container>
    </Box>
  );
}

// top header nav for menu and exit
export function MenuEventTower(props) {
  const user = props.user;
  const filters = props.filters ? props.filters : {};
  const eventContext = useContext(EventContext);
  const modules = getModules(user, eventContext.state.event.manager, filters);

  filters.mini = true;
  // filters.noText = true;
  filters.hideText = true;
  filters.floatText = true;
  filters.circle = true;
  filters.smallIcons = true;

  filters.inline = props.filters ? props.filters.inline : false;

  return (
    <Fragment>
      {modules.map((module, index) => {
        const showModule =
          props.filters && props.filters.onlyModules
            ? props.filters.onlyModules.includes(module.id)
            : true;
        return (
          <Fragment key={"nav-" + module.id}>
            {showModule && (
              <Box
                className={filters.inline ? "box-inline" : "box-default"}
                sx={
                  filters.inline
                    ? {
                        m: configEntzy.APP_SPACING_SM,
                      }
                    : {
                        height: configEntzy.AVATAR_SIZE_SM,
                        pt: configEntzy.APP_SPACING_SM3X,
                      }
                }
              >
                <ModuleButton
                  {...props}
                  key={module.id}
                  module={module}
                  index={index}
                  sourceMenu="tower"
                  filters={filters}
                />
              </Box>
            )}
          </Fragment>
        );
      })}
    </Fragment>
  );
}

export const MenuSidebarButton = (props) => {
  const icon = props.icon;
  const text = props.text;
  const subText = props.subText;
  const hideText = props.hideText;
  const bgColor = props.bgColor;
  const textColor = props.textColor;
  const counterShow = props.counterShow;
  const counterValue = props.counterValue;
  const onClick = props.onClick;

  const width = props.autoWidth
    ? "auto"
    : {
        xs: "4rem",
        sm: "6rem",
        md: "6rem",
      };

  return (
    <Button
      variant="text"
      sx={{
        width: width,
        minWidth: "0",
        pt: configEntzy.APP_SPACING_SM3X,
        pb: hideText
          ? configEntzy.APP_SPACING_SM3X
          : configEntzy.APP_SPACING_SM2X,
        mb: configEntzy.APP_SPACING_MD,
        flexDirection: "column",
        backgroundColor: bgColor
          ? bgColor === "none"
            ? undefined
            : bgColor
          : "black",
        color: textColor ? textColor : "white",
        textTransform: "none",
        fontSize: {
          xs: "1.4rem",
          sm: "2rem",
          md: "2rem",
        },
      }}
      onClick={onClick}
    >
      <span className="fa-layers fa-fw">
        <FontAwesomeIcon
          icon={icon}
          color="black"
          transform="down-2"
          style={{
            opacity: 0.6,
            filter: "drop-shadow(0.1rem 0.1rem 0.1rem black)",
          }}
        />
        <FontAwesomeIcon
          icon={icon}
          color={textColor}
          className="fa-beat"
          style={{
            "--fa-animation-duration": "1.5s",
            "--fa-beat-scale": 1.1,
          }}
        />
        {counterShow && (
          <span
            className="fa-layers-counter fa-layers-top-right"
            style={{
              backgroundColor: counterValue > 0 ? "red" : "white",
              color: counterValue > 0 ? "white" : "black",
              border: counterValue > 0 ? "1px solid red" : "1px solid black",
              // move the counter slightly right for better visibility
              transform: "translate(50%, -50%)",
              fontSize: "0.4em",
            }}
          >
            {counterValue}
          </span>
        )}
      </span>
      {!hideText && (
        <Fragment>
          <Typography
            variant="body1"
            sx={{
              mt: configEntzy.APP_SPACING_SM,
            }}
          >
            {text}
          </Typography>
          {subText && (
            <Typography
              variant="body1"
              sx={{
                mt: configEntzy.APP_SPACING_XS,
              }}
            >
              {subText}
            </Typography>
          )}
        </Fragment>
      )}
    </Button>
  );
};

export function MenuEventVerticalBar(props) {
  const user = props.user;
  const mainContext = useContext(MainContext);
  const eventContext = useContext(EventContext);
  const modules = getModules(
    user,
    eventContext.state.event.manager,
    props.filters,
  );
  const hideText = props.filters && props.filters.hideText;

  const openTransact = async () => {
    if (user.connected) {
      mainContext.setPayDirectViewer({
        open: mainContext.state.payDirect.viewer.open ? false : true,
        recipient: parseEntzyUrlComponents(eventContext.state.event.data.Url)
          .relativeUrl,
        currency: eventContext.state.event.data.Currency,
      });
    } else {
      mainContext.updateAlert({
        show: true,
        message: "You must be connected to transact",
      });
    }
  };

  return (
    <Fragment>
      {modules.map((module, index) => {
        let text = module.shortName;
        // let subText = module.shortSubtext;
        let subText = null;
        let [counterShow, counterValue] = getModuleCounter(
          module.id,
          eventContext.state.event.data.Url,
          mainContext.state.notifications,
          eventContext.state.riderQueue,
        );
        let onClick = module.actionExit
          ? () => eventContext.exitEvent({ mainContext, eventContext })
          : module.id === "transact"
            ? () => openTransact()
            : module.id === "menu" && props.menuAction
              ? props.menuAction
              : () => eventContext.focusModule(module.id);

        let bgColor = "none";
        let textColor = "white";
        if (eventContext.state.event.moduleSelected === module.id) {
          textColor = "orange";
        }

        return (
          <Box key={"nav-" + module.id} className="box-inline">
            {props.filters.onlyModules &&
            !props.filters.onlyModules.includes(module.id) ? (
              <></>
            ) : (
              <MenuSidebarButton
                {...props}
                key={module.id}
                index={index}
                icon={module.icon.main}
                text={text}
                subText={subText}
                hideText={hideText}
                bgColor={bgColor}
                textColor={textColor}
                counterShow={counterShow}
                counterValue={counterValue}
                onClick={onClick}
              />
            )}
          </Box>
        );
      })}
    </Fragment>
  );
}

export function MenuEventSpeedDial(props) {
  const user = props.user;
  const mainContext = useContext(MainContext);
  const eventContext = useContext(EventContext);
  const modules = getModules(
    user,
    eventContext.state.event.manager,
    props.filters,
  );
  const hideText = props.filters && props.filters.hideText;

  const position = props.position;
  const direction = "down";
  const tooltipPlacement = position === "left" ? "right" : "left";

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Box className="box-default">
      <Backdrop open={open} />
      <SpeedDial
        ariaLabel="SpeedDial tooltip example"
        sx={{
          position: "absolute",
          top: configEntzy.APP_SPACING_MD,
          left: configEntzy.APP_SPACING_MD,
        }}
        direction={direction}
        icon={<SpeedDialIcon />}
        onClose={handleClose}
        onOpen={handleOpen}
        open={open}
        size="large"
      >
        {modules.map((module, index) => {
          let text = module.shortName;
          let onClick = module.actionExit
            ? () => eventContext.exitEvent({ mainContext, eventContext })
            : () => eventContext.focusModule(module.id);

          let tooltipOpen = hideText ? false : true;

          return (
            <SpeedDialAction
              key={module.id}
              icon={
                <FontAwesomeIcon
                  icon={module.icon.main}
                  style={{
                    color:
                      eventContext.state.event.moduleSelected === module.id
                        ? "orange"
                        : "white",
                  }}
                />
              }
              slotProps={{
                tooltip: {
                  title: text,
                  placement: tooltipPlacement,
                  open: tooltipOpen,
                },
              }}
              tooltipTitle={text}
              tooltipOpen={tooltipOpen}
              tooltipPlacement={tooltipPlacement}
              onClick={onClick}
            />
          );
        })}
      </SpeedDial>
    </Box>
  );
}

export const MenuAccordionButton = (props) => {
  const icon = props.icon;
  const text = props.text;
  // const subText = props.subText;
  const bgColor = props.bgColor;
  const textColor = props.textColor;
  const counterShow = props.counterShow;
  const counterValue = props.counterValue;
  const selected = props.selected;
  const onClick = props.onClick;

  return (
    <Box
      className="box-default bg-white-t25 action-pointover"
      sx={{
        mb: configEntzy.APP_SPACING_XS3X,
        borderRadius: configEntzy.BORDER_SIZE_XL,
        opacity: selected ? 0.5 : 1,
      }}
    >
      <Box
        className="box-default upper-case"
        variant="text"
        sx={{
          p: configEntzy.APP_SPACING_MD,
          backgroundColor: bgColor
            ? bgColor === "none"
              ? undefined
              : bgColor
            : "black",
          color: textColor ? textColor : "white",
          borderRadius: configEntzy.BORDER_SIZE_XL,
        }}
        onClick={onClick}
      >
        <Fragment>
          <Typography variant="subtitle2">
            {icon && (
              <span className="fa-layers fa-fw fa-pull-left">
                <FontAwesomeIcon
                  icon={icon}
                  color={textColor}
                  className="fa-beat"
                  transform="grow-4 down-2"
                  style={{
                    "--fa-animation-duration": "1.5s",
                    "--fa-beat-scale": 1.1,
                  }}
                />
                {counterShow && (
                  <span
                    className="fa-layers-counter fa-layers-top-right"
                    style={{
                      backgroundColor: counterValue > 0 ? "red" : "white",
                      color: counterValue > 0 ? "white" : "black",
                      border:
                        counterValue > 0 ? "1px solid red" : "1px solid black",
                      // move the counter slightly right for better visibility
                      transform: "translate(50%, -50%)",
                      fontSize: "0.4em",
                    }}
                  >
                    {counterValue}
                  </span>
                )}
              </span>
            )}
            <span>&nbsp;&nbsp;</span>
            <span>{text}</span>
            <span>&nbsp;&nbsp;</span>
            {icon && (
              <span className="fa-layers fa-fw fa-pull-right">
                <FontAwesomeIcon
                  icon={icon}
                  style={{
                    visibility: "hidden",
                  }}
                />
              </span>
            )}
          </Typography>
          {/* <Typography
              variant="body1"
              sx={{
                mt: configEntzy.APP_SPACING_XS,
              }}
            >
              {subText}
            </Typography> */}
        </Fragment>
      </Box>
    </Box>
  );
};

export function MenuAccordionStateBar(props) {
  const user = props.user;
  // const mainContext = useContext(MainContext);
  const eventContext = useContext(EventContext);
  const modules = getModules(
    user,
    eventContext.state.event.manager,
    props.filters,
  );
  const width = Math.floor(100 / modules.length) + "%";

  return (
    <Box
      className="box-default bg-black"
      sx={{
        height: configEntzy.APP_SPACING_XS3X,
      }}
    >
      {modules.map((module, index) => {
        let bgColor = "none";
        if (eventContext.state.event.moduleSelected === module.id) {
          bgColor = "purple";
        }
        return (
          <Box
            key={"nav-" + module.id}
            className="box-inline"
            sx={{
              width: width,
              height: configEntzy.APP_SPACING_XS3X,
              backgroundColor: bgColor,
              borderRight:
                index < modules.length - 1
                  ? configEntzy.BORDER_DASH_GSM
                  : "none",
            }}
          ></Box>
        );
      })}
    </Box>
  );
}

export function MenuEventAccordion(props) {
  const user = props.user;
  const mainContext = useContext(MainContext);
  const eventContext = useContext(EventContext);
  const modules = getModules(
    user,
    eventContext.state.event.manager,
    props.filters,
  );
  const hideText = props.filters && props.filters.hideText;
  const handleToggleMenu = props.handleToggleMenu;

  const openTransact = async () => {
    if (user.connected) {
      mainContext.setPayDirectViewer({
        open: mainContext.state.payDirect.viewer.open ? false : true,
        recipient: parseEntzyUrlComponents(eventContext.state.event.data.Url)
          .relativeUrl,
        currency: eventContext.state.event.data.Currency,
      });
    } else {
      mainContext.updateAlert({
        show: true,
        message: "You must be connected to transact",
      });
    }
  };

  return (
    <Fragment>
      {modules.map((module, index) => {
        let text = module.shortName;
        let subText = module.shortSubtext;
        let [counterShow, counterValue] = getModuleCounter(
          module.id,
          eventContext.state.event.data.Url,
          mainContext.state.notifications,
          eventContext.state.riderQueue,
        );
        let onClick = module.actionExit
          ? () => eventContext.exitEvent({ mainContext, eventContext })
          : module.id === "transact"
            ? () => openTransact()
            : module.id === "menu" && props.menuAction
              ? props.menuAction
              : () => {
                  eventContext.focusModule(module.id);
                  if (handleToggleMenu) handleToggleMenu();
                };

        let bgColor = "none";
        let textColor = "white";
        if (eventContext.state.event.moduleSelected === module.id) {
          bgColor = "purple";
          textColor = "orange";
        }

        return (
          <Box key={"nav-" + module.id} className="box-default">
            {props.filters.onlyModules &&
            !props.filters.onlyModules.includes(module.id) ? (
              <></>
            ) : (
              <MenuAccordionButton
                {...props}
                key={module.id}
                index={index}
                icon={module.icon.main}
                text={text}
                subText={subText}
                hideText={hideText}
                bgColor={bgColor}
                textColor={textColor}
                counterShow={counterShow}
                counterValue={counterValue}
                onClick={onClick}
              />
            )}
          </Box>
        );
      })}
    </Fragment>
  );
}
